import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import type { FormInput } from './utm-mapping-form';

export function useValidationSchema(existingChannelNames: string[] = []) {
  const intl = useIntl();
  const normalizedChannelNames = useMemo(
    () =>
      new Set(
        existingChannelNames.map(channelName =>
          channelName.trim().toLowerCase(),
        ),
      ),
    [existingChannelNames],
  );

  const messages = {
    channelRequired: intl.formatMessage({
      defaultMessage: 'Please select a channel',
      description: 'Channel select required error message',
    }),
    channelNameRequired: intl.formatMessage({
      defaultMessage: 'Channel name is required',
      description: 'Channel name input required error message',
    }),
    channelNameAlreadyExists: intl.formatMessage({
      defaultMessage: 'Channel name already exists',
      description: 'Channel name duplicate error message',
    }),
    utmSourcesRequired: intl.formatMessage({
      defaultMessage: 'Select at least one UTM source',
      description: 'UTM sources select required error message',
    }),
  };

  return z
    .object({
      channelId: z.string().nullable(),
      isNewChannel: z.boolean(),
      utmSourceIds: z.array(z.string()),
      channelName: z.string().nullable(),
    })
    .superRefine((data: FormInput, ctx) => {
      if (!data.isNewChannel) {
        if (!data.channelId || data.channelId.trim() === '') {
          ctx.addIssue({
            code: 'custom',
            path: ['channelId'],
            message: messages.channelRequired,
          });
        }
      }

      if (data.isNewChannel) {
        if (!data.channelName || data.channelName.trim() === '') {
          ctx.addIssue({
            code: 'custom',
            path: ['channelName'],
            message: messages.channelNameRequired,
          });
        } else if (
          normalizedChannelNames.has(data.channelName.trim().toLowerCase())
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['channelName'],
            message: messages.channelNameAlreadyExists,
          });
        }
      }

      if (data.utmSourceIds.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['utmSourceIds'],
          message: messages.utmSourcesRequired,
        });
      }
    });
}
